import { useEffect, useRef, useState } from 'react';
import { tapPageIndexingSchema, tapPageLinkResponseSchema, type TapPageIndexing } from '@inktap/contracts/tap-page-indexing';

import { request } from '../lib/api';

type Props = { slug: string; slugId: string; indexing: TapPageIndexing | undefined; canManagePublicAlias?: boolean; onStatusConfirmed?: (signal: AbortSignal) => void | Promise<void> };

const reasons: Record<TapPageIndexing['reason'], string> = {
  indexable: 'Your published public page allows search engine indexing.',
  opted_out: 'Search engine indexing is off.',
  ineligible_plan: 'Search engine indexing is unavailable on your current plan.',
  missing_alias: 'A public alias is required before enabling indexing.',
  unpublished: 'Publish your page before enabling indexing.',
  inactive: 'Restore your page access before enabling indexing.',
  suspended: 'Indexing is disabled while this page is suspended.',
  artifact_unavailable: 'The published page is unavailable. Publish it again before enabling indexing.'
};

function IndexingControls({ slug, slugId, indexing, canManagePublicAlias, onStatusConfirmed }: { slug: string; slugId: string; indexing: TapPageIndexing; canManagePublicAlias: boolean; onStatusConfirmed?: (signal: AbortSignal) => void | Promise<void> }) {
  const [current, setCurrent] = useState(indexing);
  const [aliasAccess, setAliasAccess] = useState(canManagePublicAlias === true);
  const [alias, setAlias] = useState('');
  const [refreshingSharing, setRefreshingSharing] = useState(false);
  const [pending, setPending] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [feedback, setFeedback] = useState<{ failed: boolean; text: string } | null>(null);
  const operation = useRef<AbortController | null>(null);
  useEffect(() => () => operation.current?.abort(), []);

  const refreshSummary = async (controller: AbortController) => {
    if (!onStatusConfirmed) return;
    setPending(false);
    setRefreshingSharing(true);
    try {
      await onStatusConfirmed(controller.signal);
    } catch {
      // Summary failure cannot invalidate a confirmed PATCH or status readback.
    } finally {
      if (!controller.signal.aborted) setRefreshingSharing(false);
    }
  };

  const save = async (body: { indexable: boolean; publicSlug?: string }) => {
    if (operation.current || uncertain || refreshingSharing) return;
    const controller = new AbortController();
    operation.current = controller;
    setPending(true);
    setFeedback(null);
    try {
      const raw = await request<unknown>(`/pages/${encodeURIComponent(slug)}/link`, {
        method: 'PATCH', body: JSON.stringify(body), signal: controller.signal
      });
      if (controller.signal.aborted) return;
      const parsed = tapPageLinkResponseSchema.safeParse(raw);
      if (!parsed.success || parsed.data.slug !== slug || (body.publicSlug !== undefined && parsed.data.publicSlug !== body.publicSlug)) {
        setUncertain(true);
        setFeedback({ failed: true, text: 'The saved status could not be confirmed. Refresh this page before making another change.' });
        return;
      }
      setCurrent(parsed.data.indexing);
      setFeedback({ failed: false, text: parsed.data.indexing.effectiveIndexable
        ? 'Saved. Search engine indexing is allowed.'
        : parsed.data.indexing.ownerOptedIn
          ? 'Saved. Your preference is on, but indexing is currently blocked.'
          : body.publicSlug && parsed.data.publicSlug
            ? 'Public alias saved. Search engine indexing is off.'
            : 'Saved. Search engine indexing is off.' });
      if (body.publicSlug) await refreshSummary(controller);
    } catch {
      if (!controller.signal.aborted) {
        // A connection failure may happen after the server saved the change.
        setUncertain(true);
        setFeedback({ failed: true, text: 'The change could not be confirmed. Refresh this page to check the saved status before trying again.' });
      }
    } finally {
      if (!controller.signal.aborted) { operation.current = null; setPending(false); }
    }
  };

  const update = () => {
    if (!current.canEnable && !current.ownerOptedIn) return;
    void save({ indexable: !current.ownerOptedIn });
  };

  const createAlias = () => {
    if (!aliasAccess || current.reason !== 'missing_alias' || pending || uncertain) return;
    const normalized = alias.trim().toLowerCase();
    if (!/^[a-z0-9-]{3,64}$/.test(normalized)) {
      setFeedback({ failed: true, text: 'Use 3 to 64 letters, numbers or dashes for your public alias.' });
      return;
    }
    void save({ publicSlug: normalized, indexable: false });
  };

  const refresh = async () => {
    if (operation.current) return;
    const controller = new AbortController();
    operation.current = controller;
    setPending(true);
    setFeedback(null);
    try {
      const result = await request<{ tapPage?: { slugId?: string; slug?: string; indexing?: unknown }; plan?: { entitlements?: { canManagePublicAlias?: unknown } } }>('/me/dashboard', { signal: controller.signal });
      if (controller.signal.aborted) return;
      const parsed = tapPageIndexingSchema.safeParse(result?.tapPage?.indexing);
      if (!parsed.success || result?.tapPage?.slugId !== slugId || result?.tapPage?.slug !== slug) throw new Error('Unconfirmed page');
      setCurrent(parsed.data);
      setAliasAccess(result?.plan?.entitlements?.canManagePublicAlias === true);
      setUncertain(false);
      setFeedback({ failed: false, text: 'Indexing status refreshed.' });
      await refreshSummary(controller);
    } catch {
      if (!controller.signal.aborted) setFeedback({ failed: true, text: 'The saved status could not be confirmed. Try refreshing the indexing status again.' });
    } finally {
      if (!controller.signal.aborted) { operation.current = null; setPending(false); }
    }
  };

  if (!current.eligible) return null;
  return (
    <section id="tap-indexing-panel" className="tap-indexing-panel" aria-label="Search engine indexing" aria-busy={pending || refreshingSharing}>
      <fieldset disabled={pending || uncertain || refreshingSharing}>
        <legend>Search engine indexing</legend>
        <p id="tap-indexing-policy">Anyone can find your public page through search when indexing is allowed. Search engines decide whether to list it; listing is not guaranteed.</p>
        <label className="tap-indexing-panel__toggle">
          <input type="checkbox" checked={current.ownerOptedIn} disabled={!current.canEnable && !current.ownerOptedIn}
            aria-describedby="tap-indexing-policy tap-indexing-state" onChange={() => void update()} />
          <span>Allow search engines to index my public page</span>
        </label>
        <p id="tap-indexing-state">{reasons[current.reason]}</p>
      </fieldset>
      {current.reason === 'missing_alias' ? aliasAccess ? (
        <form className="tap-indexing-panel__alias" noValidate onSubmit={event => { event.preventDefault(); createAlias(); }}>
          <fieldset disabled={pending || uncertain || refreshingSharing}>
            <legend>Create your public alias</legend>
            <label htmlFor="tap-public-alias">Public alias</label>
            <input id="tap-public-alias" value={alias} maxLength={64} autoCapitalize="none" autoCorrect="off" spellCheck={false}
              aria-describedby="tap-alias-help" onChange={event => setAlias(event.target.value)} />
            <p id="tap-alias-help">Use 3 to 64 letters, numbers or dashes. This alias becomes part of your public URL. Availability is confirmed when you save. Creating it keeps indexing off until you opt in separately.</p>
            <button type="submit" className="button-secondary">Create public alias</button>
          </fieldset>
        </form>
      ) : <p>Your account cannot create a public alias with its current permissions.</p> : null}
      {current.metadata ? (
        <div className="tap-indexing-panel__preview" aria-label="Published search preview">
          <strong>{current.metadata.title}</strong>
          <p>{current.metadata.description}</p>
        </div>
      ) : null}
      {current.canonicalUrl ? <p className="tap-indexing-panel__url">Public canonical URL: <a href={current.canonicalUrl} target="_blank" rel="noopener noreferrer">{current.canonicalUrl}</a></p> : null}
      <p>Search engine caches may take time to update after you switch indexing on or off.</p>
      <p>Downgrading removes your public alias and turns indexing off. After upgrading, set up an alias and opt in again.</p>
      {uncertain ? <button type="button" className="button-secondary" disabled={pending} onClick={() => void refresh()}>Refresh indexing status</button> : null}
      {pending ? <p role="status">{uncertain ? 'Refreshing indexing status...' : 'Saving indexing preference...'}</p> : null}
      {feedback ? <p role={feedback.failed ? 'alert' : 'status'}>{feedback.text}</p> : null}
    </section>
  );
}

export function TapPageIndexingPanel(props: Props) {
  const parsed = tapPageIndexingSchema.safeParse(props.indexing);
  if (!parsed.success || !parsed.data.eligible) return null;
  // Remount on server projection or page identity changes to cancel stale responses.
  return <IndexingControls key={`${props.slugId}:${props.slug}:${JSON.stringify(parsed.data)}:${props.canManagePublicAlias === true}`} slug={props.slug} slugId={props.slugId} indexing={parsed.data} canManagePublicAlias={props.canManagePublicAlias === true} onStatusConfirmed={props.onStatusConfirmed} />;
}
